import React, { Component } from 'react';
import {
  ScrollView,
  Text,
  View,
} from 'react-native';

import Messaging from '../messaging.js';

import EonnTitle from '../components/title';
import EonnButton from '../components/button';
import EonnLink from '../components/link';
import EonnField from '../components/field';
import { mstyles } from '../constants/MainStyles';

const RESEND_COOLDOWN_SECONDS = 60;

// Mask all but the last 4 digits of an E.164 phone number for display.
const maskPhone = (phone) => {
  if (!phone) return '';
  const last4 = phone.slice(-4);
  const prefix = phone.slice(0, Math.max(0, phone.length - 4));
  return `${prefix.replace(/\d/g, '*')}${last4}`;
};

export default class Screen extends Component {

  constructor(props) {
    super(props);
    this.state = {
      code: "",
      err: "",
      info: "",
      loading: false,
      resending: false,
      resendCooldown: RESEND_COOLDOWN_SECONDS,
      showRequired: false,
    };
    this._cooldownTimer = null;
  }

  componentDidMount() {
    this.startCooldown();
  }

  componentWillUnmount() {
    this.stopCooldown();
  }

  startCooldown = () => {
    this.stopCooldown();
    this.setState({ resendCooldown: RESEND_COOLDOWN_SECONDS });
    this._cooldownTimer = setInterval(() => {
      this.setState((prev) => {
        const next = Math.max(0, prev.resendCooldown - 1);
        if (next === 0) this.stopCooldown();
        return { resendCooldown: next };
      });
    }, 1000);
  };

  stopCooldown = () => {
    if (this._cooldownTimer) {
      clearInterval(this._cooldownTimer);
      this._cooldownTimer = null;
    }
  };

  handleVerify = async () => {
    const code = (this.state.code || "").trim();
    if (!/^\d{6}$/.test(code)) {
      this.setState({ err: "Please enter the 6-digit code." });
      return;
    }
    if (!global.techSignup || !global.techSignup.verifyToken || !global.techSignup.phone) {
      this.setState({ err: "Verification session missing. Please go back and try again." });
      return;
    }

    this.setState({ loading: true, err: "", info: "" });
    try {
      const result = await Messaging.verifyPhoneCode(
        global.techSignup.verifyToken,
        code,
        global.techSignup.phone
      );
      if (!result.ok) {
        const msg = (result.body && result.body.error) || "Could not verify code. Please try again.";
        this.setState({ err: msg, loading: false });
        return;
      }
      if (result.body && result.body.verified === true) {
        global.techSignup.phoneVerified = true;
        delete global.techSignup.verifyToken;
        if (global.user) {
          global.user.phoneVerified = true;
          delete global.user.verifyToken;
        }
        this.setState({ loading: false });
        this.props.navigation.navigate("T_Profile3");
        return;
      }
      const attemptsLeft = (result.body && typeof result.body.attemptsLeft === 'number')
        ? result.body.attemptsLeft
        : null;
      const msg = attemptsLeft !== null
        ? `Incorrect code. Attempts left: ${attemptsLeft}`
        : "Incorrect code. Please try again.";
      this.setState({ err: msg, loading: false });
    } catch (e) {
      console.error('verifyPhoneCode error:', e);
      this.setState({ err: "Network error verifying code. Please try again.", loading: false });
    }
  };

  handleResend = async () => {
    if (this.state.resendCooldown > 0 || this.state.resending) return;
    if (!global.techSignup || !global.techSignup.phone) {
      this.setState({ err: "Phone number missing. Please go back and try again." });
      return;
    }
    this.setState({ resending: true, err: "", info: "" });
    try {
      const result = await Messaging.sendPhoneVerificationCode(global.techSignup.phone);
      if (!result.ok) {
        const retry = result.body && result.body.retryAfterSeconds;
        const msg = (result.body && result.body.error) || "Could not resend code. Please try again.";
        this.setState({
          err: msg,
          resending: false,
          resendCooldown: retry ? Math.max(this.state.resendCooldown, retry) : this.state.resendCooldown,
        });
        if (retry) this.startCooldown();
        return;
      }
      global.techSignup.verifyToken = result.body.token;
      this.setState({ resending: false, info: "A new code has been sent.", code: "" });
      this.startCooldown();
    } catch (e) {
      console.error('resend sendPhoneVerificationCode error:', e);
      this.setState({ err: "Network error resending code. Please try again.", resending: false });
    }
  };

  render() {
    const phone = (global.techSignup && global.techSignup.phone) || "";
    const masked = maskPhone(phone);
    const cooldown = this.state.resendCooldown;

    return (
      <ScrollView style={mstyles.sContainer} contentContainerStyle={mstyles.sContent}>
        <EonnTitle>Verify your phone</EonnTitle>

        <Text style={{
          fontSize: 16,
          fontFamily: 'nunito',
          color: '#2e303e',
          textAlign: 'center',
          marginBottom: 10,
          marginHorizontal: 20,
        }}>
          We sent a 6-digit code to {masked || 'your phone'}. Enter it below to continue.
        </Text>

        {this.state.err
          ? <Text style={mstyles.err}>{this.state.err}</Text>
          : null
        }
        {this.state.info && !this.state.err
          ? <Text style={{
              color: '#2e303e',
              fontSize: 14,
              fontFamily: 'nunito',
              textAlign: 'center',
              marginBottom: 5,
            }}>{this.state.info}</Text>
          : null
        }

        <EonnField
          onChange={value => this.setState({ code: value.replace(/\D/g, '').slice(0, 6), err: "" })}
          showRequired={this.state.showRequired}
          value={this.state.code}
          keyboardType={"number-pad"}
          inputMode={"numeric"}
          maxLength={6}
          textContentType={"oneTimeCode"}
          autoComplete={"one-time-code"}
        >Verification Code</EonnField>

        <EonnButton
          loading={this.state.loading}
          navigationProps={this.props.navigation}
          showRequired={() => this.setState({ showRequired: true })}
          required={() => [this.state.code]}
          onPress={this.handleVerify}
        >
          Verify
        </EonnButton>

        <View style={{ alignItems: 'center', marginTop: -10, marginBottom: 30 }}>
          <EonnLink
            onPress={cooldown > 0 ? () => {} : this.handleResend}
          >
            <Text style={{
              color: cooldown > 0 ? '#9597a3' : '#2e303e',
              fontSize: 16,
              fontFamily: 'nunito',
              textDecorationLine: cooldown > 0 ? 'none' : 'underline',
            }}>
              {this.state.resending
                ? 'Resending...'
                : cooldown > 0
                  ? `Resend code in ${cooldown}s`
                  : 'Resend code'}
            </Text>
          </EonnLink>

          <View style={{ height: 10 }} />

          <EonnLink onPress={() => this.props.navigation.goBack()}>
            <Text style={{
              color: '#2e303e',
              fontSize: 16,
              fontFamily: 'nunito',
              textDecorationLine: 'underline',
            }}>
              Change phone number
            </Text>
          </EonnLink>
        </View>
      </ScrollView>
    );
  }
}
